import type { Requirement, RequirementTheme } from '@ispot/shared'
import { useRequirementsViewStore } from '@/stores/requirementsViewStore'
import { SidebarFacetList, type FacetItem } from '../SidebarFacetList'

interface Props {
  requirements: Requirement[]
  themes: RequirementTheme[]
}

const TYPE_OPTIONS: FacetItem[] = [
  { value: 'epic',        label: 'Epic' },
  { value: 'story',       label: 'Story' },
  { value: 'nfr-category', label: 'NFR Category' },
]

const PRIORITY_OPTIONS: FacetItem[] = [
  { value: 'critical', label: 'Critical', glyph: <Dot color="var(--color-danger)" /> },
  { value: 'high',     label: 'High',     glyph: <Dot color="var(--color-warning)" /> },
  { value: 'medium',   label: 'Medium',   glyph: <Dot color="var(--color-warm-500)" /> },
  { value: 'low',      label: 'Low',      glyph: <Dot color="var(--color-warm-300)" /> },
]

const STATUS_OPTIONS: FacetItem[] = [
  { value: 'backlog',     label: 'Backlog' },
  { value: 'planned',     label: 'Planned' },
  { value: 'in-progress', label: 'In progress' },
  { value: 'in-review',   label: 'In review' },
  { value: 'done',        label: 'Done' },
]

/**
 * Floating filter popover used by the Tree, Document, and Table view toolbars.
 * Reads/writes store facets directly — single source of truth across views.
 *
 * Anchoring is the parent's responsibility; this component renders the panel
 * itself (positioned absolute by the parent).
 */
export function FilterPopover({ requirements, themes }: Props) {
  const facetTypes = useRequirementsViewStore(s => s.facetTypes)
  const facetPriorities = useRequirementsViewStore(s => s.facetPriorities)
  const facetStatuses = useRequirementsViewStore(s => s.facetStatuses)
  const facetThemeIds = useRequirementsViewStore(s => s.facetThemeIds)
  const toggleFacet = useRequirementsViewStore(s => s.toggleFacet)
  const clearFacets = useRequirementsViewStore(s => s.clearFacets)

  const counts = countFacets(requirements)
  const typeItems     = TYPE_OPTIONS.map(o => ({ ...o, count: counts.type[o.value] ?? 0 }))
  const priorityItems = PRIORITY_OPTIONS.map(o => ({ ...o, count: counts.priority[o.value] ?? 0 }))
  const statusItems   = STATUS_OPTIONS.map(o => ({ ...o, count: counts.status[o.value] ?? 0 }))
  const themeItems: FacetItem[] = themes.map(t => ({
    value: t.id,
    label: t.name,
    count: counts.themes[t.id] ?? 0,
    glyph: <Dot color={t.color || 'var(--color-warm-500)'} />,
  }))

  const total = facetTypes.size + facetPriorities.size + facetStatuses.size + facetThemeIds.size

  return (
    <div
      role="dialog"
      aria-label="Filters"
      className="rounded-lg overflow-y-auto"
      style={{
        width: 280,
        maxHeight: 480,
        background: 'var(--color-bg-card)',
        border: '1px solid var(--color-border)',
        boxShadow: '0 8px 32px -8px rgba(29,26,21,0.18), 0 2px 6px -2px rgba(29,26,21,0.06)',
        padding: 12,
      }}
    >
      <div className="flex items-center justify-between mb-2">
        <span
          style={{
            fontSize: 11,
            fontWeight: 600,
            color: 'var(--color-fg-muted)',
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
          }}
        >
          Filters
        </span>
        <div className="flex items-center gap-1">
          {total > 0 && (
            <button
              onClick={clearFacets}
              className="text-[11px] font-medium px-1.5 py-0.5 rounded cursor-pointer border-0 bg-transparent"
              style={{ color: 'var(--color-fg-muted)' }}
            >
              Clear
            </button>
          )}
        </div>
      </div>

      <SidebarFacetList
        title="Type"
        items={typeItems}
        selected={facetTypes}
        onToggle={(v) => toggleFacet('types', v)}
      />
      <SidebarFacetList
        title="Priority"
        items={priorityItems}
        selected={facetPriorities}
        onToggle={(v) => toggleFacet('priorities', v)}
      />
      <SidebarFacetList
        title="Status"
        items={statusItems}
        selected={facetStatuses}
        onToggle={(v) => toggleFacet('statuses', v)}
      />
      {themeItems.length > 0 && (
        <SidebarFacetList
          title={`Themes · ${themeItems.length}`}
          items={themeItems}
          selected={facetThemeIds}
          onToggle={(v) => toggleFacet('themeIds', v)}
        />
      )}
    </div>
  )
}

function Dot({ color }: { color: string }) {
  return <span style={{ width: 8, height: 8, borderRadius: '50%', background: color, display: 'inline-block' }} />
}

function countFacets(requirements: Requirement[]) {
  const type: Record<string, number> = {}
  const priority: Record<string, number> = {}
  const status: Record<string, number> = {}
  const themes: Record<string, number> = {}
  for (const r of requirements) {
    type[r.type] = (type[r.type] ?? 0) + 1
    if (r.priority) priority[r.priority] = (priority[r.priority] ?? 0) + 1
    status[r.status] = (status[r.status] ?? 0) + 1
    for (const id of r.themeIds) themes[id] = (themes[id] ?? 0) + 1
  }
  return { type, priority, status, themes }
}
